import { useEffect, useState } from 'react';
import './NudosMaskedNumberInput.scss';
import React from 'react';
import { formatCost, formatRoundedUpNumber, roundUpNumber } from '../../../utils/formatNumbers';

/**
 * React functional component corresponding to the Nudos text input
 * @property {funcion(inputText: string): void} handleChange - a callback to execute upon changing the input text (only the number, not the masked is pass as value)
 * @property {string} currentValue
 * @property {string} placeholder
 * @property {string} maskPrefix - Optional, a string to add BEFORE the number in the masked state of the component. If no prefix is required pass the empty string "".
 * @property {string} maskSufix - Optional, a string to add AFTER the number in the masked state of the component. If no sufix is required pass the empty string "".
 * @property {boolean} shouldRoundUp - Optional, a boolean indicating if the number should be rounded up
 * @property {boolean} roundToClosest - Optional, a boolean indicating if the number should be rounded to its closest value. Wont necessarily be up.
 * @property {number} numberOfDecimals - Optional, the number of decimals, default is 0
 * @property {funcion(unformattedNumber?: string): string} customMaskerFunction - Optional, a callback that accepts the unformatted number string of the input and returns a formatted string to display when the component isnt focused
 * @property {string | JSX.Element} label - Optional
 * @property {boolean} isFilled - Optional, boolean indicating if the input is filled. Should be passed to change the style from empty to filled. Its value should reflect the change in the input value. Default reflect the state of the currentValue.
 * @property {string} errorText - Optional, the text to be shown if theres an error with the input form. If an error is passed the style of the component will change to reflect that.
 * @property {string} componentSize - Optional, one of the standard sizes for the components, corresponding to one of the following words: "extraSmall", "small", "medium" or "large". If no value is passed the component will have the width of its parent container.
 * @property {boolean} isDeactivated - Optional, boolean indicating if the input is deactivated. If a value is provided the input will behave as a read only field and styles will be accordingly.
 * @property {string} customClassName - Optional, a className to customize styles
 * @property {boolean} hideErrorText - Optional, if the dropdown is part of another component that shows an error text, used this boolean to prevent from repeating the error text.
 * @property {funcion(): void} handleBlur - Optional, a callback to pass to the onBlur event for the input component
 * @property {string} inputWidth - Optional, the number of pixels for the input width
 * @property {string} inputHeight - Optional, the number of pixels for the input height
 * @property {string} inputPadding - Optional, the number of pixels for the input horizontal padding
 */
const NudosMaskedNumberInput = ({
  handleChange,
  currentValue,
  placeholder,
  maskPrefix,
  maskSufix,
  shouldRoundUp,
  roundToClosest,
  numberOfDecimals,
  customMaskerFunction,
  label,
  isFilled,
  errorText,
  componentSize,
  isDeactivated,
  customClassName,
  hideErrorText,
  handleBlur,
  inputWidth,
  inputHeight,
  inputPadding
}: InudosMaskedNumberInput) => {
  const [inputNumberValue, setInputNumberValue] = useState<string>();
  const [inputMaskedValue, setInputMaskedValue] = useState<string>();
  const [isFocused, setIsFocused] = useState(false);

  const className = customClassName ? customClassName : '';
  const errorStyles = errorText && errorText.length ? 'error' : '';
  const filledStyles =
    (isFilled || currentValue || (inputNumberValue && inputNumberValue.length > 0)) && !isDeactivated ? 'filled' : '';
  const deactivatedStyles = isDeactivated ? 'deactivated' : '';
  const sizeStyles = componentSize ? componentSize : '';
  const customWidthStyle = {
    minWidth: inputWidth ? `${inputWidth}px` : undefined,
    maxWidth: inputWidth ? `${inputWidth}px` : undefined
  };
  const inputStyles = {
    ...customWidthStyle,
    height: inputHeight ? `${inputHeight}px` : undefined,
    paddingRight: inputPadding ? `${inputPadding}px` : undefined,
    paddingLeft: inputPadding ? `${inputPadding}px` : undefined
  };

  const inputType = isFocused ? 'number' : undefined;

  const roundNumber = (unroundedNumber?: string) => {
    if (!unroundedNumber) return;
    if (shouldRoundUp) return `${roundUpNumber(+unroundedNumber, numberOfDecimals || 0)}`;
    if (roundToClosest) return (+unroundedNumber).toFixed(numberOfDecimals);
    return unroundedNumber;
  };

  const formatInputNumber = (unformattedNumber?: string) => {
    if (!unformattedNumber) return;
    if (shouldRoundUp) return formatRoundedUpNumber(+unformattedNumber, numberOfDecimals || 0);
    if (roundToClosest) return formatCost(+(+unformattedNumber).toFixed(numberOfDecimals), numberOfDecimals);
    return formatCost(+unformattedNumber, numberOfDecimals);
  };

  const maskerFunction = (unformattedNumber?: string) => {
    if (!unformattedNumber) return;
    if (customMaskerFunction) return customMaskerFunction(unformattedNumber);
    return `${maskPrefix}${formatInputNumber(unformattedNumber)}${maskSufix}`;
  };

  const handleBlurCallback = () => {
    maskerFunction(inputNumberValue);
    setIsFocused(false);
    handleChange && handleChange(roundNumber(inputNumberValue));
    handleBlur && handleBlur(inputNumberValue || '');
  };

  const handleFocus = () => setIsFocused(true);

  const handleInputChange = (event: React.SyntheticEvent) => {
    const { value } = event.target as HTMLInputElement;
    if (Math.sign(Number(value)) === -1) return setInputNumberValue('');
    setInputNumberValue(value);
    isFocused && handleChange && handleChange(value);
  };

  const handlePressEnter = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = event.currentTarget as HTMLInputElement;
      target.blur();
    }
  };

  useEffect(() => {
    setInputNumberValue(currentValue);
  }, [currentValue]);

  useEffect(() => {
    setInputMaskedValue(maskerFunction(currentValue || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  return (
    <div
      className={`nudosMaskedNumberInput ${errorStyles} ${filledStyles} ${deactivatedStyles} ${sizeStyles} ${className}`}
      onBlur={handleBlurCallback}
      onFocus={handleFocus}
      style={customWidthStyle}
    >
      {label && typeof label === 'string' && <label className="labelSize">{label}</label>}
      {label && typeof label !== 'string' && React.isValidElement(label) && label}
      <input
        style={inputStyles}
        placeholder={placeholder}
        readOnly={isDeactivated}
        onChange={handleInputChange}
        value={isFocused ? inputNumberValue : inputNumberValue ? inputMaskedValue : ''}
        type={inputType}
        onKeyDown={event => handlePressEnter(event)}
      />
      {errorText && !hideErrorText && errorText.length > 0 && <div className="errorText">{errorText}</div>}
    </div>
  );
};

export default NudosMaskedNumberInput;

export interface InudosMaskedNumberInput {
  handleChange?: (inputText?: string) => void;
  currentValue?: string;
  placeholder?: string;
  maskPrefix?: string;
  maskSufix?: string;
  shouldRoundUp?: boolean;
  roundToClosest?: boolean;
  numberOfDecimals?: number;
  customMaskerFunction?: (unformattedNumber?: string) => string;
  label?: string | JSX.Element;
  isFilled?: boolean;
  errorText?: string;
  componentSize?: string;
  isDeactivated?: boolean;
  customClassName?: string;
  hideErrorText?: boolean;
  handleBlur?: (inputText: string) => void;
  inputWidth?: number;
  inputHeight?: number;
  inputPadding?: number;
}
